/* Connections: the calendars Amon reads and writes, the address your plan is
 * published at, and the task tools it reads and ticks off.
 *
 * Calendars start from the one thing everybody knows — their email address.
 * Amon suggests the way in that works for it (Sign in with Google, Sign in with
 * Microsoft, an iCloud app password) and keeps every other way one tap away,
 * because a guess about somebody's company mail setup is sometimes wrong.
 *
 * Two rules kept from the prototype:
 *
 *   · **The server says what a connection reads and writes.** The catalogue is
 *     fetched, not written out here — a permissions sentence maintained in a
 *     second place is a sentence that will one day be wrong.
 *   · **Unbuilt tools are listed, not hidden,** with "Ask for it".
 */

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del, errorMessage } from '../../api';
import { useToast } from '../../ui/toast';
import { useSession } from '../../session';
import Section from '../../ui/Section';
import Dialog from '../../ui/Dialog';
import Icon from '../../ui/Icon';
import type { IconName } from '../../ui/Icon';
import { ConfirmDialog } from '../../ui/schedule';

interface CatalogueEntry {
  id: string; name: string; subtitle: string | null;
  kind: 'calendar' | 'tasks' | 'other'; kindLabel: string;
  connect: string; state: 'live' | 'dormant' | 'planned';
  reads: string | null; writes: string | false; note: string;
  requestable: boolean; requested: boolean; requests: number | null;
}

interface CalendarAccount {
  id: string; provider: string; label: string; status: string;
  lastSyncAt: number | null; lastError: string | null; live: boolean;
  canWrite?: boolean; needsReconnectToWrite?: boolean;
  writeCalendar?: string | null; writeError?: string | null;
}

interface Providers { ics: boolean; google: boolean; microsoft?: boolean; caldav?: boolean }

interface Detected {
  provider: 'google' | 'microsoft' | 'icloud' | 'fastmail' | 'yahoo' | null;
  connect?: 'oauth' | 'caldav'; label?: string; button?: string; preset?: string;
  domain: string; via: string; configured: Providers;
}

interface ProviderCalendar { id: string; summary: string; primary: boolean; canWrite?: boolean }

interface FieldHint { label: string; placeholder: string; help: string }
interface TaskProvider {
  id: string; label: string; fields: ('site' | 'email' | 'token')[];
  guide: { tokenUrl: string; where: string; note: string; fieldHints: Record<string, FieldHint> } | null;
  canComplete: boolean; canReopen: boolean;
}
interface TaskConnection {
  id: string; provider: string; label: string; site: string | null;
  account: string | null; lastSyncAt?: number | null; lastError?: string | null;
}
interface Feed { url: string; fetchCount: number }

type Preset = 'icloud' | 'fastmail' | 'yahoo' | 'other';

const PROVIDER_ICON: Record<string, IconName> = {
  google: 'calendar', microsoft: 'monitor', caldav: 'globe', ics: 'link',
  jira: 'flag', todoist: 'check2', linear: 'zap'
};

const PRESETS: { value: Preset; label: string; help: string; url: string | null }[] = [
  { value: 'icloud', label: 'iCloud', url: 'https://account.apple.com/account/manage',
    help: 'At account.apple.com → Sign-In and Security → App-Specific Passwords → make one called "Amon". Two-factor authentication has to be on.' },
  { value: 'fastmail', label: 'Fastmail', url: 'https://app.fastmail.com/settings/security/devicekeys',
    help: 'In Fastmail → Settings → Privacy & Security → Manage app passwords → New app password, with calendar access.' },
  { value: 'yahoo', label: 'Yahoo', url: 'https://login.yahoo.com/myaccount/security/',
    help: 'In your Yahoo account → Account security → Generate app password.' },
  { value: 'other', label: 'Other', url: null,
    help: 'Any calendar server that speaks CalDAV: Nextcloud, Zoho, Synology and others. Your provider\'s help pages list the server address.' }
];

function since(ms: number | null | undefined): string {
  if (!ms) return 'not synced yet';
  const m = Math.round((Date.now() - ms) / 60000);
  if (m < 2) return 'synced just now';
  if (m < 60) return `synced ${m} min ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `synced ${h}h ago` : `synced ${Math.round(h / 24)}d ago`;
}

const REASONS: Record<string, string> = {
  not_configured: 'That sign-in is not set up on this server yet.',
  expired: 'The sign-in took too long. Try again.',
  session_changed: 'You signed in as somebody else part-way through. Try again.',
  identity_failed: 'The provider did not say who the account belongs to. Try again.',
  no_refresh_token: 'The provider did not allow staying connected. Try again and accept every permission.'
};

export default function Connections() {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useSession();
  const [params, setParams] = useSearchParams();
  const [icsUrl, setIcsUrl] = useState('');
  const [connecting, setConnecting] = useState<TaskProvider | null>(null);
  const [dropping, setDropping] = useState<{ kind: 'calendar' | 'tasks'; id: string; label: string } | null>(null);
  const [rotating, setRotating] = useState(false);
  const [caldavPreset, setCaldavPreset] = useState<Preset | null>(null);
  const [writingFor, setWritingFor] = useState<CalendarAccount | null>(null);

  /* Back from Google or Microsoft: say what happened, once. */
  useEffect(() => {
    const status = params.get('calendar');
    if (!status) return;
    if (status === 'connected') {
      const n = Number(params.get('imported') || 0);
      toast.say(`Calendar connected${n ? ` — ${n} events read` : ''}. Choose a calendar to write your plan to below.`, 'good');
    } else if (status === 'cancelled') {
      toast.say('Not connected — you cancelled on the sign-in page.');
    } else {
      toast.say(REASONS[params.get('reason') || ''] || 'That calendar could not be connected.', 'bad');
    }
    const next = new URLSearchParams(params);
    ['calendar', 'imported', 'context', 'reason', 'provider'].forEach(k => next.delete(k));
    setParams(next, { replace: true });
    void qc.invalidateQueries({ queryKey: ['calendars'] });
  }, []);                                                          // eslint-disable-line react-hooks/exhaustive-deps

  const catalogue = useQuery({
    queryKey: ['integrations-catalogue'],
    queryFn: async () => {
      const r = await get<{ providers: CatalogueEntry[] }>('/api/integrations/catalogue');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read the list of connections.'));
      return r.providers;
    }
  });

  const calendars = useQuery({
    queryKey: ['calendars'],
    queryFn: async () => {
      const r = await get<{ accounts: CalendarAccount[]; providers: Providers }>('/api/calendars');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your calendars.'));
      return r;
    }
  });

  const taskTools = useQuery({
    queryKey: ['task-connections'],
    queryFn: async () => {
      const r = await get<{ connections: TaskConnection[]; providers: TaskProvider[] }>('/api/integrations/tasks');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your task tools.'));
      return r;
    }
  });

  const feed = useQuery({
    queryKey: ['feed'],
    queryFn: async () => {
      const r = await get<{ feed: Feed }>('/api/calendar/feed');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your calendar address.'));
      return r.feed;
    }
  });

  const refreshAll = () => {
    for (const key of ['calendars', 'task-connections', 'integrations-catalogue', 'tasks', 'plan', 'week', 'month'])
      void qc.invalidateQueries({ queryKey: [key] });
  };

  const addIcs = useMutation({
    mutationFn: async () => {
      const r = await post<{ imported?: number }>('/api/calendars/ics', { url: icsUrl.trim() });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read that calendar.'));
      return r;
    },
    onSuccess: r => {
      setIcsUrl('');
      refreshAll();
      toast.say(`Calendar linked${typeof r.imported === 'number' ? ` — ${r.imported} events read` : ''}.`, 'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const syncCalendars = useMutation({
    mutationFn: async () => {
      const r = await post('/api/calendars/sync', {});
      if (!r.ok) throw new Error(errorMessage(r, 'Could not sync.'));
      await post('/api/calendars/push', {});
      return r;
    },
    onSuccess: () => { refreshAll(); toast.say('Calendars synced both ways.', 'good'); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const syncTasks = useMutation({
    mutationFn: async () => {
      const r = await post('/api/integrations/tasks/sync', {});
      if (!r.ok) throw new Error(errorMessage(r, 'Could not sync.'));
      return r;
    },
    onSuccess: () => { refreshAll(); toast.say('Task tools synced.', 'good'); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const drop = async (d: { kind: 'calendar' | 'tasks'; id: string; label: string }) => {
    const r = await del(d.kind === 'calendar' ? `/api/calendars/${d.id}` : `/api/integrations/tasks/${d.id}`);
    if (!r.ok) throw new Error(errorMessage(r, 'Could not disconnect that.'));
    refreshAll();
    toast.say(`${d.label} is disconnected.`);
  };

  const rotate = async () => {
    const r = await post('/api/calendar/feed/rotate', {});
    if (!r.ok) throw new Error(errorMessage(r, 'Could not make a new address.'));
    void qc.invalidateQueries({ queryKey: ['feed'] });
    toast.say('New address. Every calendar subscribed to the old one has stopped updating.');
  };

  const request = useMutation({
    mutationFn: async (c: CatalogueEntry) => {
      const r = c.requested
        ? await del(`/api/integrations/request/${c.id}`)
        : await post('/api/integrations/request', { provider: c.id });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not record that.'));
      return c;
    },
    onSuccess: c => {
      void qc.invalidateQueries({ queryKey: ['integrations-catalogue'] });
      toast.say(c.requested ? `Took back the request for ${c.name}.` : `Noted — ${c.name} is on the list.`, 'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const cat = catalogue.data ?? [];
  const accounts = calendars.data?.accounts ?? [];
  const providers: Providers = calendars.data?.providers ?? { ics: true, google: false };
  const connections = taskTools.data?.connections ?? [];
  const taskProviders = taskTools.data?.providers ?? [];
  const planned = cat.filter(c => c.state === 'planned');
  const writing = accounts.filter(a => a.writeCalendar).length;

  return (
    <>
      {/* ── calendars, both ways ───────────────────────────────── */}
      <Section id="calendars-title" icon="calendar" title="Calendars"
               meta={accounts.length
                 ? `${accounts.length} connected${writing ? ` · your plan is written to ${writing}` : ' · reading only'}`
                 : 'read your meetings, and write your plan back'}
               info={<>Amon reads your calendars so it never plans over a meeting. Choose a calendar
                 to write to and your plan appears there too — on your phone, your watch, and to
                 anyone checking whether you are free. Only events Amon made are ever changed, and
                 disconnecting takes them back out.</>}
               action={accounts.length > 0 && (
                 <button className="btn btn-ghost btn-icon" onClick={() => syncCalendars.mutate()}
                         disabled={syncCalendars.isPending}
                         aria-label="Sync calendars now" title="Sync calendars now">
                   <Icon name="refresh" size={17} />
                 </button>
               )}>

        {accounts.length > 0 && (
          <ul className="item-list">
            {accounts.map(a => (
              <li key={a.id} className="item calendar-account">
                <span className="item-icon" aria-hidden="true">
                  <Icon name={PROVIDER_ICON[a.provider] ?? 'calendar'} size={17} />
                </span>
                <div className="item-body">
                  <strong>{a.label}</strong>
                  <span className={`small ${a.lastError ? 'push-failed' : 'muted'}`}>
                    {a.lastError ? `Last sync failed: ${a.lastError}` : since(a.lastSyncAt)}
                    {a.live ? ' · live updates on' : ''}
                  </span>
                  {a.writeCalendar && !a.writeError && (
                    <span className="small write-state"><Icon name="send" size={12} />Your plan is written here</span>
                  )}
                  {a.writeError && (
                    <span className="small push-failed">Could not write your plan: {a.writeError.replace(/_/g, ' ')}</span>
                  )}
                  {a.needsReconnectToWrite && (
                    <a className="small linkish" href="/api/calendars/google/start?reconnect=1">
                      Reconnect to let Amon write your plan here
                    </a>
                  )}
                </div>
                <div className="item-tools">
                  {a.canWrite && (
                    <button className="btn btn-ghost btn-sm" onClick={() => setWritingFor(a)}>
                      <Icon name="send" size={15} />{a.writeCalendar ? 'Writing' : 'Write plan'}
                    </button>
                  )}
                  <button className="btn btn-ghost btn-icon btn-bad"
                          aria-label={`Disconnect ${a.label}`} title="Disconnect"
                          onClick={() => setDropping({ kind: 'calendar', id: a.id, label: a.label })}>
                    <Icon name="trash" size={17} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <ConnectByEmail defaultEmail={user?.email || ''} providers={providers}
                        onCaldav={p => setCaldavPreset(p)} />

        <details className="more-ways">
          <summary><Icon name="chevronDown" size={14} />A calendar link instead (.ics, read only)</summary>
          <form className="inline wrap connect-ics" onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (icsUrl.trim()) addIcs.mutate();
          }}>
            <span className="input-with-icon">
              <Icon name="link" size={16} />
              <input value={icsUrl} maxLength={600} inputMode="url"
                     placeholder="Paste a calendar link (.ics)"
                     aria-label="Calendar link"
                     onChange={e => setIcsUrl(e.target.value)} />
            </span>
            <button className="btn btn-primary btn-sm" type="submit"
                    disabled={!icsUrl.trim() || addIcs.isPending}>
              <Icon name="plus" size={16} />{addIcs.isPending ? 'Reading it…' : 'Link it'}
            </button>
          </form>
        </details>
      </Section>

      {/* ── your plan out, by subscription ─────────────────────── */}
      <Section id="feed-title" icon="globe" title="Your plan as a subscription"
               meta={feed.data?.fetchCount
                 ? `subscribed · read ${feed.data.fetchCount} times`
                 : 'for any calendar app, without signing in'}
               info={<>Subscribe to this address in any calendar app and your plan shows up there,
                 read-only. Useful for a calendar Amon cannot sign in to. Anyone holding the address
                 can read your plan, so treat it like a password — and make a new one if it leaks.</>}>
        {feed.data && (
          <div className="feed-row">
            <input readOnly value={feed.data.url} aria-label="Your calendar address"
                   onFocus={e => e.currentTarget.select()} />
            <button className="btn btn-ghost btn-sm"
                    onClick={() => {
                      void navigator.clipboard?.writeText(feed.data!.url)
                        .then(() => toast.say('Address copied. Paste it into "subscribe to calendar".', 'good'))
                        .catch(() => toast.say(feed.data!.url));
                    }}>
              <Icon name="copy" size={16} />Copy
            </button>
            <button className="btn btn-ghost btn-icon" onClick={() => setRotating(true)}
                    aria-label="Make a new address" title="Make a new address">
              <Icon name="refresh" size={17} />
            </button>
          </div>
        )}
      </Section>

      {/* ── task tools ─────────────────────────────────────────── */}
      <Section id="tools-title" icon="plug" title="Task tools"
               meta={connections.length
                 ? `${connections.length} connected · ticking off here ticks off there`
                 : 'Jira, Todoist and Linear'}
               info={<>Amon imports what is open and assigned to you, plans it, and when you finish
                 something here it is marked done in the tool too. Nothing else is ever written.</>}
               action={connections.length > 0 && (
                 <button className="btn btn-ghost btn-icon" onClick={() => syncTasks.mutate()}
                         disabled={syncTasks.isPending}
                         aria-label="Sync task tools now" title="Sync task tools now">
                   <Icon name="refresh" size={17} />
                 </button>
               )}>

        {connections.length > 0 && (
          <ul className="item-list">
            {connections.map(c => (
              <li key={c.id} className="item">
                <span className="item-icon" aria-hidden="true">
                  <Icon name={PROVIDER_ICON[c.provider] ?? 'plug'} size={17} />
                </span>
                <div className="item-body">
                  <strong>{c.label}</strong>
                  <span className={`small ${c.lastError ? 'push-failed' : 'muted'}`}>
                    {c.lastError ? `Last sync failed: ${c.lastError}`
                      : [c.site, c.account, since(c.lastSyncAt)].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <div className="item-tools">
                  <button className="btn btn-ghost btn-icon btn-bad"
                          aria-label={`Disconnect ${c.label}`} title="Disconnect"
                          onClick={() => setDropping({ kind: 'tasks', id: c.id, label: c.label })}>
                    <Icon name="trash" size={17} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="connect-grid">
          {taskProviders.map(p => (
            <button key={p.id} className="connect-card" onClick={() => setConnecting(p)}>
              <span className="item-icon" aria-hidden="true"><Icon name={PROVIDER_ICON[p.id] ?? 'plug'} size={18} /></span>
              <span className="connect-text">
                <strong>{p.label}</strong>
                <span className="muted small">
                  {p.canComplete && p.canReopen ? 'Imports · ticks off · re-opens'
                    : p.canComplete ? 'Imports · ticks off' : 'Imports'}
                </span>
              </span>
              <Icon name="plus" size={16} />
            </button>
          ))}
        </div>
      </Section>

      {planned.length > 0 && (
        <Section id="later-title" icon="star" className="tone-slate" title="Not built yet"
                 meta="ask for the ones you would use — it decides what comes next"
                 info={<>These are on the list and not built. Asking is counted, and it is the most
                   direct say you have in the order they arrive.</>}>
          <ul className="item-list">
            {planned.map(c => (
              <li key={c.id} className="item">
                <span className="item-icon" aria-hidden="true">
                  <Icon name={c.kind === 'calendar' ? 'calendar' : c.kind === 'tasks' ? 'tasks' : 'plug'} size={17} />
                </span>
                <div className="item-body">
                  <strong>{c.name}</strong>
                  <span className="muted small" title={c.note}>
                    {c.subtitle ?? c.kindLabel}
                    {c.requests ? ` · asked for by ${c.requests}` : ''}
                  </span>
                </div>
                <div className="item-tools">
                  <button className="btn btn-ghost btn-sm" aria-pressed={c.requested}
                          disabled={request.isPending} onClick={() => request.mutate(c)}>
                    <Icon name={c.requested ? 'check' : 'hobbies'} size={15} />
                    {c.requested ? 'Asked' : 'Ask for it'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {caldavPreset && (
        <CaldavDialog preset={caldavPreset} defaultUsername={user?.email || ''}
                      onClose={() => setCaldavPreset(null)}
                      onConnected={n => { refreshAll(); toast.say(`Connected — ${n} events read. Choose a calendar to write your plan to.`, 'good'); }} />
      )}

      {writingFor && (
        <WriteDialog account={writingFor} onClose={() => setWritingFor(null)} onSaved={refreshAll} />
      )}

      {connecting && (
        <ConnectDialog provider={connecting}
                       onClose={() => setConnecting(null)}
                       onConnected={label => { refreshAll(); toast.say(`${label} connected. Your open items are on the Tasks screen.`, 'good'); }} />
      )}

      {dropping && (
        <ConfirmDialog
          title={`Disconnect ${dropping.label}?`}
          description={dropping.kind === 'calendar'
            ? 'Its events come off your days, and anything Amon wrote into it is taken back out. You can connect it again at any time.'
            : 'Tasks imported from it stay, but stop syncing — finishing one here will no longer tick it off there.'}
          confirmLabel="Disconnect"
          danger
          onClose={() => setDropping(null)}
          onConfirm={() => drop(dropping)} />
      )}

      {rotating && (
        <ConfirmDialog
          title="Make a new calendar address?"
          description="Every calendar subscribed to the current address stops updating, and you will need to subscribe again with the new one. Use this if the address has leaked."
          confirmLabel="Make a new one"
          danger
          onClose={() => setRotating(false)}
          onConfirm={rotate} />
      )}
    </>
  );
}

/* ── start from the address ─────────────────────────────────── */

export function ConnectByEmail({ defaultEmail, providers, onCaldav }: {
  defaultEmail: string;
  providers: Providers;
  onCaldav: (preset: Preset) => void;
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [asked, setAsked] = useState(defaultEmail);

  /* The account's address arrives with the session, which can be a moment
     after this first renders. Adopt it then — but never over something the
     person has already typed. */
  useEffect(() => {
    if (defaultEmail && !email && !asked) { setEmail(defaultEmail); setAsked(defaultEmail); }
  }, [defaultEmail]);                                              // eslint-disable-line react-hooks/exhaustive-deps

  const detect = useQuery({
    queryKey: ['calendar-detect', asked],
    enabled: /@.+\./.test(asked),
    queryFn: async () => {
      const r = await get<Detected>(`/api/calendars/detect?email=${encodeURIComponent(asked)}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not check that address.'));
      return r;
    }
  });

  const d = detect.data;
  const hint = encodeURIComponent(asked);

  const google = (primary: boolean) => providers.google
    ? <a className={`btn btn-sm ${primary ? 'btn-primary' : 'btn-ghost'}`} href="/api/calendars/google/start">
        <Icon name="calendar" size={15} />Sign in with Google
      </a>
    : <span className="btn btn-sm btn-ghost is-disabled" aria-disabled="true" title="Waiting on setup">
        <Icon name="calendar" size={15} />Google — coming soon
      </span>;

  const microsoft = (primary: boolean) => providers.microsoft
    ? <a className={`btn btn-sm ${primary ? 'btn-primary' : 'btn-ghost'}`} href={`/api/calendars/microsoft/start?email=${hint}`}>
        <Icon name="monitor" size={15} />Sign in with Microsoft
      </a>
    : <span className="btn btn-sm btn-ghost is-disabled" aria-disabled="true" title="Waiting on setup">
        <Icon name="monitor" size={15} />Microsoft — coming soon
      </span>;

  const icloud = (primary: boolean, preset: Preset = 'icloud', label = 'Connect iCloud') => (
    <button type="button" className={`btn btn-sm ${primary ? 'btn-primary' : 'btn-ghost'}`} onClick={() => onCaldav(preset)}>
      <Icon name="globe" size={15} />{label}
    </button>
  );

  let suggested: JSX.Element | null = null;
  let others: JSX.Element[] = [];
  if (d?.provider === 'google') { suggested = google(true); others = [microsoft(false), icloud(false)]; }
  else if (d?.provider === 'microsoft') { suggested = microsoft(true); others = [google(false), icloud(false)]; }
  else if (d?.provider === 'icloud') { suggested = icloud(true); others = [google(false), microsoft(false)]; }
  else if (d?.provider === 'fastmail' || d?.provider === 'yahoo') {
    suggested = icloud(true, d.provider, d.button || 'Connect');
    others = [google(false), microsoft(false), icloud(false)];
  } else {
    others = [google(false), microsoft(false), icloud(false), icloud(false, 'other', 'Other CalDAV')];
  }

  return (
    <div className="connect-email">
      <form className="inline wrap" onSubmit={(e: FormEvent) => { e.preventDefault(); setAsked(email.trim()); }}>
        <span className="input-with-icon">
          <Icon name="send" size={16} />
          <input type="email" value={email} maxLength={200}
                 placeholder="The email your calendar uses"
                 aria-label="The email your calendar uses"
                 onChange={e => setEmail(e.target.value)}
                 onBlur={() => setAsked(email.trim())} />
        </span>
      </form>

      {detect.isFetching && <p className="muted small">Checking which calendar that is…</p>}

      {d && (
        <div className="connect-suggest" role="status">
          {suggested && (
            <p className="small">
              <strong>{d.label}</strong>
              <span className="muted"> · {d.via === 'mx' ? `${d.domain} uses it for email` : 'recognised from the address'}</span>
            </p>
          )}
          {!suggested && <p className="muted small">Not sure which calendar {d.domain} uses — pick one:</p>}
          <div className="inline wrap">
            {suggested}
            {others.map((o, i) => <span key={i}>{o}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── iCloud and other CalDAV ────────────────────────────────── */

function CaldavDialog({ preset: initial, defaultUsername, onClose, onConnected }: {
  preset: Preset;
  defaultUsername: string;
  onClose: () => void;
  onConnected: (imported: number) => void;
}) {
  const [preset, setPreset] = useState<Preset>(initial);
  const [username, setUsername] = useState(defaultUsername);
  const [password, setPassword] = useState('');
  const [server, setServer] = useState('https://');
  const [error, setError] = useState<string | null>(null);
  const chosen = PRESETS.find(p => p.value === preset)!;

  const connect = useMutation({
    mutationFn: async () => {
      const r = await post<{ imported: number }>('/api/calendars/caldav', {
        preset, username: username.trim(), password, ...(preset === 'other' ? { server: server.trim() } : {})
      });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not connect that calendar.'));
      return r;
    },
    onSuccess: r => { onConnected(r.imported || 0); onClose(); },
    onError: (e: Error) => setError(e.message)
  });

  return (
    <Dialog title={`Connect ${chosen.label === 'Other' ? 'a CalDAV calendar' : chosen.label}`} icon="globe"
            description="Uses an app-specific password — not your normal password — which you can revoke on its own at any time."
            onClose={onClose}>
      <form className="when-form" onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (!username.trim() || !password) { setError('Both the username and the app password are needed.'); return; }
        setError(null);
        connect.mutate();
      }}>
        <fieldset className="field-wide repeat-picker">
          <legend>Which</legend>
          {PRESETS.map(p => (
            <label key={p.value} className={`chip chip-pick${preset === p.value ? ' is-on' : ''}`}>
              <input type="radio" name="caldav-preset" checked={preset === p.value} onChange={() => setPreset(p.value)} />
              {p.label}
            </label>
          ))}
        </fieldset>

        <div className="field-wide caldav-guide">
          <p className="muted small"><Icon name="info" size={13} /> {chosen.help}</p>
          {chosen.url && (
            <a className="btn btn-ghost btn-sm" href={chosen.url} target="_blank" rel="noreferrer noopener">
              <Icon name="lock" size={14} />Make an app password
            </a>
          )}
        </div>

        {preset === 'other' && (
          <div className="field field-wide">
            <label htmlFor="caldav-server">Server address</label>
            <input id="caldav-server" value={server} inputMode="url" onChange={e => setServer(e.target.value)} />
          </div>
        )}

        <div className="field field-wide">
          <label htmlFor="caldav-user">{preset === 'icloud' ? 'Apple Account email' : 'Username'}</label>
          <input id="caldav-user" type="email" value={username} autoComplete="username"
                 onChange={e => setUsername(e.target.value)} />
        </div>

        <div className="field field-wide">
          <label htmlFor="caldav-pass">App-specific password</label>
          <input id="caldav-pass" type="password" value={password} autoComplete="off"
                 placeholder="xxxx-xxxx-xxxx-xxxx" onChange={e => setPassword(e.target.value)} />
        </div>

        {error && <p className="auth-err field-wide" role="alert">{error}</p>}

        <div className="inline field-wide">
          <button className="btn btn-primary btn-sm" type="submit" disabled={connect.isPending}>
            <Icon name="plug" size={15} />{connect.isPending ? 'Signing in…' : 'Connect'}
          </button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Dialog>
  );
}

/* ── where the plan is written ──────────────────────────────── */

function WriteDialog({ account, onClose, onSaved }: {
  account: CalendarAccount;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [choice, setChoice] = useState<string | null>(account.writeCalendar ?? null);

  const list = useQuery({
    queryKey: ['account-calendars', account.id],
    queryFn: async () => {
      const r = await get<{ calendars: ProviderCalendar[]; writing: string | null }>(`/api/calendars/${account.id}/calendars`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not list the calendars on that account.'));
      return r;
    }
  });

  const save = useMutation({
    mutationFn: async () => {
      const r = await put<{ pushed?: { created: number; failed: number; error: string | null } }>(
        `/api/calendars/${account.id}/write`, { calendarId: choice });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change that.'));
      return r;
    },
    onSuccess: r => {
      onSaved();
      if (!choice) toast.say('Amon stopped writing to that calendar and took its events back out.');
      else if (r.pushed?.failed) toast.say(`Writing is on, but ${r.pushed.failed} events could not be written: ${r.pushed.error}.`, 'bad');
      else toast.say(`Your plan is on that calendar — ${r.pushed?.created ?? 0} events for the next two weeks.`, 'good');
      onClose();
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const writable = (list.data?.calendars ?? []).filter(c => c.canWrite !== false);

  return (
    <Dialog title="Write your plan to…" icon="send"
            description="Your focus time, rituals and plans appear as events, tagged Amon. Only those events are ever changed."
            onClose={onClose}>
      <div className="when-form">
        {list.isPending && <p className="muted small field-wide">Reading the calendars on {account.label}…</p>}
        {list.isError && <p className="auth-err field-wide" role="alert">{(list.error as Error).message}</p>}
        {list.data && (
          <fieldset className="field-wide slot-picker">
            <legend className="sr-only">Calendar</legend>
            <label className={`slot-option${choice === null ? ' is-on' : ''}`}>
              <input type="radio" name="write-cal" checked={choice === null} onChange={() => setChoice(null)} />
              <span className="slot-when"><strong>Don't write — read only</strong></span>
            </label>
            {writable.map(c => (
              <label key={c.id} className={`slot-option${choice === c.id ? ' is-on' : ''}`}>
                <input type="radio" name="write-cal" checked={choice === c.id} onChange={() => setChoice(c.id)} />
                <span className="slot-when">
                  <strong>{c.summary}</strong>
                  {c.primary && <span className="muted small">main calendar</span>}
                </span>
              </label>
            ))}
          </fieldset>
        )}
        <p className="muted small field-wide">
          Tip: make a calendar called "Amon" in your calendar app and choose it here, so your plan can be shown or hidden in one tap.
        </p>
        <div className="inline field-wide">
          <button className="btn btn-primary btn-sm" disabled={save.isPending || list.isPending}
                  onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </Dialog>
  );
}

/* ── task tool connection ───────────────────────────────────── */

function ConnectDialog({ provider, onClose, onConnected }: {
  provider: TaskProvider;
  onClose: () => void;
  onConnected: (label: string) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const hints = provider.guide?.fieldHints ?? {};

  const connect = useMutation({
    mutationFn: async () => {
      const body: Record<string, string> = { provider: provider.id };
      for (const f of provider.fields) body[f] = (values[f] ?? '').trim();
      const r = await post('/api/integrations/tasks', body);
      if (!r.ok) throw new Error(errorMessage(r, `Could not connect ${provider.label}.`));
      return r;
    },
    onSuccess: () => { onConnected(provider.label); onClose(); },
    onError: (e: Error) => setError(e.message)
  });

  return (
    <Dialog title={`Connect ${provider.label}`} icon={PROVIDER_ICON[provider.id] ?? 'plug'}
            description={provider.guide?.note} onClose={onClose}>
      <form className="when-form" onSubmit={(e: FormEvent) => {
        e.preventDefault();
        const missing = provider.fields.find(f => !(values[f] ?? '').trim());
        if (missing) { setError(`${hints[missing]?.label ?? missing} is needed.`); return; }
        setError(null);
        connect.mutate();
      }}>
        {provider.guide && (
          <a className="connect-card field-wide" href={provider.guide.tokenUrl} target="_blank" rel="noreferrer noopener">
            <span className="item-icon" aria-hidden="true"><Icon name="lock" size={17} /></span>
            <span className="connect-text">
              <strong>Get your token</strong>
              <span className="muted small">{provider.guide.where}</span>
            </span>
            <Icon name="right" size={16} />
          </a>
        )}
        {provider.fields.map(f => (
          <div className="field field-wide" key={f}>
            <label htmlFor={`connect-${f}`}>{hints[f]?.label ?? f}</label>
            <input id={`connect-${f}`} type={f === 'token' ? 'password' : f === 'email' ? 'email' : 'text'}
                   autoComplete="off" spellCheck={false} placeholder={hints[f]?.placeholder}
                   value={values[f] ?? ''} onChange={e => setValues(v => ({ ...v, [f]: e.target.value }))} />
            {hints[f]?.help && <span className="muted small">{hints[f].help}</span>}
          </div>
        ))}
        {error && <p className="auth-err field-wide" role="alert">{error}</p>}
        <div className="inline field-wide">
          <button className="btn btn-primary btn-sm" type="submit" disabled={connect.isPending}>
            <Icon name="plug" size={16} />{connect.isPending ? 'Checking it…' : 'Connect'}
          </button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Dialog>
  );
}
